import React from 'react';
import { ArrowRight, PenTool, Package, Settings, FileCheck, Wrench, Check } from 'lucide-react';
import { Button } from './ui/button';
import { services, amcPlans } from '../data/mock';

const ServicesSection = () => {
  const iconMap = {
    PenTool: PenTool,
    Package: Package,
    Settings: Settings,
    FileCheck: FileCheck,
    Wrench: Wrench
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="section-padding bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block text-[#B8956B] font-medium text-sm uppercase tracking-[0.2em] mb-4">
            Our Services
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#252A32] mb-6 heading-display">
            Complete Elevator <span className="text-[#B8956B] italic">Solutions</span>
          </h2>
          <div className="elegant-divider mx-auto mb-6" />
          <p className="text-[#404752] text-lg">
            From initial design to ongoing maintenance, we provide end-to-end services 
            for all your elevator needs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Package;
            return (
              <div 
                key={service.id}
                className="group bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-all"
              >
                <div className="service-icon w-14 h-14 rounded-xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                  <IconComponent className="w-6 h-6 text-[#B8956B]" />
                </div>
                <h3 className="text-xl font-semibold text-[#252A32] mb-3">{service.title}</h3>
                <p className="text-[#404752] leading-relaxed">{service.description}</p>
              </div>
            );
          })}
        </div>

        {/* AMC Plans */}
        <div className="mb-20">
          <div className="text-center mb-14">
            <h3 className="text-2xl md:text-3xl font-semibold text-[#252A32] mb-4 heading-display">
              Annual Maintenance Contracts
            </h3>
            <p className="text-[#404752]">Keep your elevator running smoothly with our maintenance plans</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {amcPlans.map((plan) => (
              <div 
                key={plan.id}
                className={`relative rounded-3xl p-8 border transition-all ${
                  plan.popular 
                    ? 'bg-[#252A32] border-[#B8956B]/30 shadow-2xl' 
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-[#B8956B] text-white text-xs font-medium px-4 py-1.5 rounded-full tracking-wider">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-8">
                  <h4 className={`text-2xl font-semibold mb-2 heading-display ${
                    plan.popular ? 'text-white' : 'text-[#252A32]'
                  }`}>
                    {plan.name}
                  </h4>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className={`text-5xl font-semibold heading-display ${
                      plan.popular ? 'text-[#B8956B]' : 'text-[#252A32]'
                    }`}>
                      {plan.rate}
                    </span>
                    <span className={`text-lg ${
                      plan.popular ? 'text-gray-400' : 'text-[#404752]'
                    }`}>
                      {plan.description}
                    </span>
                  </div>
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                        plan.popular ? 'bg-[#B8956B]/15' : 'bg-[#B8956B]/10'
                      }`}>
                        <Check className="w-3 h-3 text-[#B8956B]" />
                      </div>
                      <span className={plan.popular ? 'text-gray-300' : 'text-[#404752]'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <Button 
                  onClick={scrollToContact}
                  className={`w-full tracking-wide ${
                    plan.popular 
                      ? 'btn-gold text-white border-0' 
                      : 'bg-[#252A32] text-white hover:bg-[#252A32]/90'
                  }`}
                >
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Warranty Banner */}
        <div className="bg-[#252A32] rounded-2xl p-10 lg:p-14">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6 text-center lg:text-left">
            <div>
              <h3 className="text-2xl lg:text-3xl font-semibold text-white mb-2 heading-display">1 Year Warranty & Free Service</h3>
              <p className="text-gray-400">All elevators come with comprehensive warranty from date of installation</p>
            </div>
            <Button 
              onClick={scrollToContact}
              className="btn-gold text-white font-medium px-8 border-0 tracking-wide"
            >
              Schedule Installation
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
